import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  HandCoins, ArrowRight, Loader2, AlertCircle, RefreshCw,
  Pencil, MapPin, Users, Clock, FileText, Info,
  ClipboardList, CheckCircle2, RotateCcw, Send, XCircle, Reply,
} from "lucide-react";
import {
  FUNDING_STATUS_LABEL, FUNDING_STATUS_TONE, FUNDING_EVENT_LABEL,
  formatSAR, formatArabicDateTime,
} from "./fundingLabels";

const CONTRIBUTION_LABEL = {FINANCIAL:"مساهمة مالية",IN_KIND:"مساهمة عينية",SPONSORSHIP:"رعاية",OTHER:"أخرى"};
const BENEFICIARY_LABEL = {STUDENTS:"طلاب",ASSOCIATIONS:"جمعيات",BOTH:"طلاب وجمعيات"};
const SCOPE_LABEL = {KINGDOM_WIDE:"على مستوى المملكة",REGION:"منطقة محددة",CITY:"مدينة محددة"};
const DURATION_UNIT_LABEL = {WEEKS:"أسبوع", MONTHS:"شهر"};
function labelValue(m,v){return v?(m[v]||v):"—";}

export default function FundingRequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [row, setRow] = useState(null);
  const [events, setEvents] = useState(null);
  const [publishedPlan, setPublishedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [busy, setBusy] = useState(null); // 'accept'|'revision'|'withdraw'|'provide_info'
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionReason, setRevisionReason] = useState("");
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoMessage, setInfoMessage] = useState("");

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [rReq, rEv] = await Promise.all([
        api.get(`/funding/requests/${id}`),
        api.get(`/funding/requests/${id}/events`),
      ]);
      const r = rReq.data.request;
      setRow(r);
      setEvents(Array.isArray(rEv.data.events) ? rEv.data.events : []);
      if (r.current_published_plan_id) {
        try {
          const rp = await api.get(`/funding/plans/${r.current_published_plan_id}`);
          setPublishedPlan(rp.data.plan);
        } catch { setPublishedPlan(null); }
      } else { setPublishedPlan(null); }
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const canAccept = row?.status === "PLAN_PUBLISHED";
  const canRevision = row?.status === "PLAN_PUBLISHED";
  const canWithdraw = row && ["SUBMITTED","UNDER_REVIEW","INFORMATION_REQUIRED","PLAN_IN_PREPARATION","PLAN_READY"].includes(row.status) && !row.current_published_plan_id;
  const canProvideInfo = row?.status === "INFORMATION_REQUIRED";

  async function acceptPlan() {
    if (busy || !publishedPlan) return;
    setBusy("accept");
    try {
      await api.post(`/funding/plans/${publishedPlan.id}/accept`, { _expected_lock_version: row.lock_version });
      toast.success("تم قبول الخطة");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }
  async function requestRevision() {
    if (busy || !publishedPlan) return;
    if (!revisionReason.trim()) { toast.error("سبب طلب التعديل مطلوب"); return; }
    setBusy("revision");
    try {
      await api.post(`/funding/plans/${publishedPlan.id}/request-revision`, {
        _expected_lock_version: row.lock_version, reason: revisionReason.trim(),
      });
      toast.success("تم إرسال طلب التعديل");
      setRevisionOpen(false); setRevisionReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }
  async function withdraw() {
    if (busy) return;
    if (!withdrawReason.trim()) { toast.error("سبب السحب مطلوب"); return; }
    setBusy("withdraw");
    try {
      await api.post(`/funding/requests/${row.id}/withdraw`, {
        _expected_lock_version: row.lock_version, reason: withdrawReason.trim(),
      });
      toast.success("تم سحب الطلب");
      setWithdrawOpen(false); setWithdrawReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }
  async function provideInfo() {
    if (busy) return;
    if (!infoMessage.trim()) { toast.error("الرسالة مطلوبة"); return; }
    setBusy("provide_info");
    try {
      await api.post(`/funding/requests/${row.id}/provide-info`, {
        _expected_lock_version: row.lock_version, message: infoMessage.trim(),
      });
      toast.success("تم إرسال المعلومات المطلوبة");
      setInfoOpen(false); setInfoMessage("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="funding-details-page">
        <button type="button" onClick={() => navigate("/dashboard/funding/requests")}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3"
          data-testid="details-back-btn">
          <ArrowRight className="h-3 w-3" /> العودة إلى قائمة الطلبات
        </button>

        {loading && (
          <div className="flex items-center justify-center py-24" data-testid="details-loading">
            <Loader2 className="h-7 w-7 animate-spin text-teal-700" />
          </div>
        )}

        {!loading && error && (
          <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200" data-testid="details-error">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-rose-700 shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-heading font-bold text-rose-900">تعذّر تحميل تفاصيل الطلب</h3>
                <p className="text-sm text-rose-800 mt-1">{error}</p>
                <div className="flex gap-2 mt-3">
                  <Button variant="outline" onClick={load} data-testid="details-retry">
                    <RefreshCw className="h-4 w-4 ms-2" /> إعادة المحاولة
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && row && (
          <>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="h-11 w-11 rounded-xl bg-amber-50 text-amber-700 grid place-items-center shrink-0">
                  <HandCoins className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold uppercase tracking-widest text-teal-700" data-testid="details-request-number">
                    {row.request_number || "مسودة"}
                  </div>
                  <h1 className="mt-1 text-2xl font-heading font-extrabold text-slate-900 break-words">
                    {row.funding_goal || "—"}
                  </h1>
                  <div className="mt-2"><StatusPill status={row.status} /></div>
                </div>
              </div>
              {row.status === "DRAFT" && (
                <Button onClick={() => navigate(`/dashboard/funding/requests/${row.id}/edit`)}
                  className="bg-teal-700 hover:bg-teal-800 whitespace-nowrap" data-testid="btn-continue-draft">
                  <Pencil className="h-4 w-4 ms-2" /> إكمال المسودة
                </Button>
              )}
            </div>

            {/* Summary */}
            <div className="grid gap-4 sm:grid-cols-2 mb-6">
              <SummaryCard icon={FileText} label="نوع المساهمة" value={labelValue(CONTRIBUTION_LABEL, row.contribution_type)} testId="sum-contribution" />
              <SummaryCard icon={HandCoins} label="القيمة التقديرية" value={formatSAR(row.estimated_amount_or_value)} testId="sum-amount" />
              <SummaryCard icon={Users} label="المستفيد" value={<BeneficiarySummary row={row} />} testId="sum-beneficiary" />
              <SummaryCard icon={MapPin} label="النطاق الجغرافي" value={<ScopeSummary row={row} />} testId="sum-scope" />
              <SummaryCard icon={Clock} label="مدة التنفيذ" value={<DurationSummary tep={row.target_execution_period} />} testId="sum-duration" />
              <SummaryCard icon={Info} label="تحديث الحالة" value={formatArabicDateTime(row.updated_at || row.created_at)} testId="sum-updated" />
            </div>

            {row.request_details && (
              <div className="mb-6 p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="details-block-request">
                <h3 className="font-heading font-bold text-slate-900 mb-2">تفاصيل الطلب</h3>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{row.request_details}</p>
              </div>
            )}

            {/* Published plan */}
            {publishedPlan && (
              <div className="mb-6 p-4 rounded-2xl bg-emerald-50/40 border border-emerald-200" data-testid="published-plan-block">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-heading font-bold text-emerald-900 flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" /> خطة التمويل المنشورة
                    </h3>
                    <p className="text-xs text-emerald-800 mt-1">
                      نسخة #{publishedPlan.version_number} · نُشرت {formatArabicDateTime(publishedPlan.published_at)}
                    </p>
                  </div>
                </div>
                <div className="rounded-xl bg-white border border-emerald-100 p-4 space-y-3 max-h-[400px] overflow-y-auto">
                  {Object.entries(publishedPlan.content || {}).map(([k, v]) => (
                    <div key={k} data-testid={`plan-section-${k}`}>
                      <div className="text-xs font-bold text-emerald-900 uppercase tracking-wider">{k}</div>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{typeof v === "string" ? v : JSON.stringify(v)}</p>
                    </div>
                  ))}
                </div>
                {(canAccept || canRevision) && (
                  <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    {canAccept && (
                      <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={acceptPlan} disabled={!!busy} data-testid="btn-accept-plan">
                        {busy === "accept" ? <Loader2 className="h-4 w-4 animate-spin ms-2" /> : <CheckCircle2 className="h-4 w-4 ms-2" />} قبول الخطة
                      </Button>
                    )}
                    {canRevision && (
                      <Button variant="outline" onClick={() => setRevisionOpen(true)} disabled={!!busy} data-testid="btn-open-revision">
                        <RotateCcw className="h-4 w-4 ms-2" /> طلب تعديل الخطة
                      </Button>
                    )}
                  </div>
                )}
                {revisionOpen && (
                  <div className="mt-4 p-4 rounded-xl bg-white border border-amber-200" data-testid="revision-form">
                    <Label className="text-sm font-semibold">سبب طلب التعديل <span className="text-rose-600">*</span></Label>
                    <Textarea rows={3} value={revisionReason} onChange={(e)=>setRevisionReason(e.target.value)} placeholder="اذكر التعديلات المطلوبة بوضوح…" data-testid="input-revision-reason" className="mt-1" />
                    <div className="flex gap-2 mt-3">
                      <Button onClick={requestRevision} disabled={!!busy} className="bg-amber-700 hover:bg-amber-800" data-testid="btn-submit-revision">
                        {busy === "revision" ? <Loader2 className="h-4 w-4 animate-spin ms-2" /> : <Send className="h-4 w-4 ms-2" />} إرسال طلب التعديل
                      </Button>
                      <Button variant="outline" onClick={()=>{setRevisionOpen(false); setRevisionReason("");}} data-testid="btn-cancel-revision">إلغاء</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Provide-info CTA */}
            {canProvideInfo && (
              <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200" data-testid="provide-info-block">
                <div className="flex items-start gap-3">
                  <Reply className="h-6 w-6 text-amber-700 shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="font-heading font-bold text-amber-900">بحاجة إلى معلومات إضافية</h3>
                    <p className="text-sm text-amber-800 mt-1">راجع طلبات الإدارة في سجل الحالة ثم قدّم الرد.</p>
                    {!infoOpen ? (
                      <Button className="mt-3 bg-amber-700 hover:bg-amber-800" onClick={()=>setInfoOpen(true)} data-testid="btn-open-provide-info">
                        <Reply className="h-4 w-4 ms-2" /> تقديم المعلومات
                      </Button>
                    ) : (
                      <div className="mt-3">
                        <Label className="text-sm font-semibold">الرسالة <span className="text-rose-600">*</span></Label>
                        <Textarea rows={4} value={infoMessage} onChange={(e)=>setInfoMessage(e.target.value)} data-testid="input-provide-info-message" className="mt-1" />
                        <div className="flex gap-2 mt-3">
                          <Button onClick={provideInfo} disabled={!!busy} className="bg-amber-700 hover:bg-amber-800" data-testid="btn-submit-provide-info">
                            {busy === "provide_info" ? <Loader2 className="h-4 w-4 animate-spin ms-2" /> : <Send className="h-4 w-4 ms-2" />} إرسال
                          </Button>
                          <Button variant="outline" onClick={()=>{setInfoOpen(false); setInfoMessage("");}} data-testid="btn-cancel-provide-info">إلغاء</Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Withdraw */}
            {canWithdraw && (
              <div className="mb-6" data-testid="withdraw-block">
                {!withdrawOpen ? (
                  <Button variant="outline" onClick={()=>setWithdrawOpen(true)} className="text-rose-700 border-rose-300 hover:bg-rose-50" data-testid="btn-open-withdraw">
                    <XCircle className="h-4 w-4 ms-2" /> سحب الطلب
                  </Button>
                ) : (
                  <div className="p-4 rounded-xl bg-white border border-rose-200">
                    <Label className="text-sm font-semibold">سبب السحب <span className="text-rose-600">*</span></Label>
                    <Textarea rows={3} value={withdrawReason} onChange={(e)=>setWithdrawReason(e.target.value)} data-testid="input-withdraw-reason" className="mt-1" />
                    <div className="flex gap-2 mt-3">
                      <Button onClick={withdraw} disabled={!!busy} className="bg-rose-700 hover:bg-rose-800" data-testid="btn-submit-withdraw">
                        {busy === "withdraw" ? <Loader2 className="h-4 w-4 animate-spin ms-2" /> : <XCircle className="h-4 w-4 ms-2" />} تأكيد السحب
                      </Button>
                      <Button variant="outline" onClick={()=>{setWithdrawOpen(false); setWithdrawReason("");}} data-testid="btn-cancel-withdraw">إلغاء</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Timeline */}
            <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="events-timeline">
              <h3 className="font-heading font-bold text-slate-900 mb-4">سجل الحالة</h3>
              {events == null ? (
                <div className="py-6 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-teal-700" /></div>
              ) : events.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="events-empty">لا توجد أحداث مسجّلة بعد.</p>
              ) : (
                <ol className="relative border-s-2 border-cream-200 space-y-5 ps-5">
                  {events.map((e) => (
                    <li key={e.id} className="relative" data-testid={`event-${e.id}`}>
                      <span className="absolute -start-[27px] top-1.5 h-3.5 w-3.5 rounded-full bg-teal-600 border-2 border-white shadow"></span>
                      <div className="text-sm font-semibold text-slate-900">
                        {FUNDING_EVENT_LABEL[e.event_type] || e.event_type}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {formatArabicDateTime(e.created_at)}
                        {e.from_status && e.to_status && (<> · {FUNDING_STATUS_LABEL[e.from_status] || e.from_status} → {FUNDING_STATUS_LABEL[e.to_status] || e.to_status}</>)}
                      </div>
                      {e.message && (<div className="mt-1.5 text-sm text-slate-700 whitespace-pre-wrap">{e.message}</div>)}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatusPill({ status }) {
  const label = FUNDING_STATUS_LABEL[status] || status || "—";
  const tone = FUNDING_STATUS_TONE[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold ${tone}`} data-testid={`details-status-${status || "unknown"}`}>{label}</span>;
}
function SummaryCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs text-slate-500 font-semibold">
        <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.75} /><span>{label}</span>
      </div>
      <div className="mt-1.5 text-sm text-slate-900 font-semibold break-words">{value ?? "—"}</div>
    </div>
  );
}
function BeneficiarySummary({ row }) {
  const parts = [labelValue(BENEFICIARY_LABEL, row.beneficiary_type)];
  if (row.students_count != null) parts.push(`طلاب: ${row.students_count}`);
  if (row.associations_count != null) parts.push(`جمعيات: ${row.associations_count}`);
  return <span>{parts.join(" · ")}</span>;
}
function ScopeSummary({ row }) {
  const parts = [labelValue(SCOPE_LABEL, row.geographic_scope)];
  if (row.region) parts.push(row.region);
  if (row.city) parts.push(row.city);
  return <span>{parts.join(" · ")}</span>;
}
function DurationSummary({ tep }) {
  if (!tep || !tep.duration_value) return "—";
  const unit = DURATION_UNIT_LABEL[tep.duration_unit] || tep.duration_unit;
  return <span>{tep.duration_value} {unit}</span>;
}
